import { BRIGHTPATTERN_TENANT_URL } from '@/config/globals';

export type BrightPatternApi = {
  init: (url: string) => void;
  selectService: (serviceName: string) => void;
  dialNumber: (phone: string) => void;
  // Persistent listener: fires every time an outbound dial genuinely starts
  // in Agent Desktop (the SDK's _O_CALL_DIALING broadcast).
  addOutboundCallDialingHandler: (callback: (event?: unknown) => void) => void;
};

export type BrightPatternStatus = 'unconfigured' | 'loading' | 'ready' | 'error';

declare global {
  interface Window {
    bpspat?: { api: BrightPatternApi };
  }
}

// Marks a missing tenant URL as an expected, already-understood outcome
// (rather than a genuine SDK failure) -- callers branch on this via
// `instanceof` so an unconfigured deployment never gets logged or surfaced
// like a real load error.
export class BrightPatternUnconfiguredError extends Error {
  constructor() {
    super('BrightPattern tenant URL is not configured.');
    this.name = 'BrightPatternUnconfiguredError';
  }
}

// Verified against the live tenant: the SDK serves from /agentdesktop/libs/
// (the previously assumed /agentdesktop/app/lib/ 404s).
const SDK_PATH = '/agentdesktop/libs/servicepattern-sdk-v1.js';

// Module-scope cache: the promise (so every racing caller shares the same
// in-flight/settled load instead of injecting a second <script>) and the
// element itself (so tests can tear it down without a full module reset).
// There is no timer-based retry by design -- a reload is the only supported
// retry path (see the loader's docstring below).
let status: BrightPatternStatus = BRIGHTPATTERN_TENANT_URL ? 'loading' : 'unconfigured';
let sdkPromise: Promise<BrightPatternApi> | null = null;
let scriptEl: HTMLScriptElement | null = null;

export const getBrightPatternStatus = (): BrightPatternStatus => status;

const injectScript = (tenantUrl: string): Promise<BrightPatternApi> =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${tenantUrl}${SDK_PATH}`;
    script.async = true;

    script.onload = () => {
      const api = window.bpspat?.api;
      if (!api) {
        status = 'error';
        reject(new Error('BrightPattern SDK script loaded but window.bpspat.api is missing.'));
        return;
      }

      api.init(tenantUrl);
      status = 'ready';
      resolve(api);
    };

    script.onerror = () => {
      status = 'error';
      reject(new Error('Failed to load the BrightPattern Agent Desktop SDK script.'));
    };

    scriptEl = script;
    document.head.appendChild(script);
  });

/**
 * Loads the BrightPattern Agent Desktop SDK and initializes it against the
 * configured tenant, caching the in-flight/resolved promise at module scope
 * so N racing callers (e.g. the dashboard and a lead detail tracker mounting
 * in the same tab) share exactly one <script> tag and one init() call.
 *
 * Rejects immediately -- with no script ever injected -- when no tenant URL
 * is configured; callers that want to distinguish "not set up" from "failed
 * to load" can check `error instanceof BrightPatternUnconfiguredError`.
 *
 * There is no retry: once the shared promise settles (ready or error),
 * every subsequent call returns that same settled promise. A page reload is
 * the only supported way to try again.
 */
export const loadBrightPatternSdk = (): Promise<BrightPatternApi> => {
  if (!BRIGHTPATTERN_TENANT_URL) {
    return Promise.reject(new BrightPatternUnconfiguredError());
  }

  if (!sdkPromise) {
    sdkPromise = injectScript(BRIGHTPATTERN_TENANT_URL);
  }

  return sdkPromise;
};

/** Test-only seam: clears the module-scope cache (promise, script tag, and
 * derived status) so each test can start from a clean slate without paying
 * for a full `vi.resetModules()` + dynamic re-import -- mirrors
 * `resetMockEcho()`'s role for the Echo double. */
export const resetBrightPatternForTests = (): void => {
  scriptEl?.remove();
  scriptEl = null;
  sdkPromise = null;
  status = BRIGHTPATTERN_TENANT_URL ? 'loading' : 'unconfigured';
};
